import React, { useEffect, useState, useMemo, useRef } from 'react';
import styles from './styles.module.css';
import { Spinner } from 'react-bootstrap';
import { getCurrentYear } from '../../utils/getItemFromRange';
import { itemsPerPage } from '../../utils/const';
import { MdRefresh } from 'react-icons/md';
import { FaRegTrashAlt } from 'react-icons/fa';
import { usePermission } from '../../hooks';
import HaventPermission from '../../assets/HaventPermission';
import usePageWidth from '../shared/usePageWidth';
import CustomSelect from '../CustomSelect';

// «Все» в CustomSelect — пустая строка, бэк ждёт 0 как «без фильтра» — см. toParam
const ALL = '';

const initialFilters = {
    xp_key: null,
    idfaculty: ALL,
    idcurriculum: ALL,
    cid: ALL,
    page: 0
};

// перечни приходят разными методами — идентификатор и наименование у каждого свои
const optionsOf = (list, valueKey, labelKeys) => {
    const seen = new Set();
    return (Array.isArray(list) ? list : [])
        .map(item => ({
            value: item[valueKey],
            name: labelKeys.map(key => item[key]).find(Boolean) || ''
        }))
        .filter(({ value }) => {
            if (value == null || seen.has(value)) return false;
            seen.add(value);
            return true;
        });
};

const byName = (a, b) => a.name.localeCompare(b.name, 'ru');

// в запрос уходит 0, а не пустая строка
const toParam = value => (value === ALL || value == null ? 0 : value);

// многоточие ставится только там, где пропущено больше одной страницы
const pagerItems = (page, pages) => {
    if (pages <= 1) return [];
    const keep = new Set([1, pages, page - 1, page, page + 1]);
    if (page <= 4) [2, 3, 4, 5].forEach(i => keep.add(i));
    if (page >= pages - 3) [pages - 1, pages - 2, pages - 3, pages - 4].forEach(i => keep.add(i));
    const list = [...keep].filter(i => i >= 1 && i <= pages).sort((a, b) => a - b);

    const items = [];
    list.forEach((value, i) => {
        if (i) {
            const gap = value - list[i - 1];
            if (gap === 2) items.push(list[i - 1] + 1);
            else if (gap > 2) items.push('gap');
        }
        items.push(value);
    });
    return items;
};

const ComplexTopicsDisciplines = ({
    years,
    getYears,
    facultyList,
    fetchFacultyList,
    curriculum,
    fetchCurriculum,
    disciplines,
    fetchDisciplines,
    clearDisciplines,

    complex,
    pagesCount,
    totalCount,
    isLoading,
    errorStatus,
    getComplexTopicsDisciplines,
    clearComplex,

    history,
    location,
}) => {

    usePageWidth('pageWide');

    const { readRight } = usePermission('Справочники', 'Комплексный тем.план (КТП) по дисциплинам');

    // возврат из карточки по «← Назад» — условия отбора и страница списка сохраняются
    const restored = useRef(location?.state?.complexDisciplinesList || null);

    const [state, setState] = useState(restored.current?.filters || initialFilters);
    const [defaultYear, setDefaultYear] = useState(restored.current?.defaultYear ?? null);
    const [loaded, setLoaded] = useState(!!restored.current?.loaded);

    const yearOptions = useMemo(
        () => optionsOf(years, 'xp_key', ['name'])
            .sort((a, b) => b.name.localeCompare(a.name, 'ru')),
        [years]
    );

    const facultyOptions = useMemo(
        () => optionsOf(facultyList, 'idfaculty', ['faculty', 'name']).sort(byName),
        [facultyList]
    );

    const planOptions = useMemo(
        () => optionsOf(curriculum, 'idcurriculum', ['cname', 'name', 'title']).sort(byName),
        [curriculum]
    );

    const disciplineOptions = useMemo(
        () => optionsOf(disciplines, 'cid', ['title', 'predmet', 'name']).sort(byName),
        [disciplines]
    );

    useEffect(() => {
        getYears();
        fetchFacultyList();
        return () => clearComplex();
    }, []);

    // при заходе в режим подставляется текущий учебный год, запрос за списком не отправляется
    useEffect(() => {
        if (!years?.length) return;
        const currentYear = getCurrentYear(years)?.xp_key ?? null;
        setDefaultYear(currentYear);
        setState(prev => prev.xp_key === null ? {...prev, xp_key: currentYear} : prev);
    }, [years]);

    // перечень учебных планов — по факультету; после смены года заполняется заново
    useEffect(() => {
        if (state.xp_key === null) return;
        fetchCurriculum(toParam(state.idfaculty));
    }, [state.xp_key, state.idfaculty]);

    // перечень дисциплин запрашивается только после выбора учебного плана:
    // при нуле бэк отдаёт общий перечень всех планов в другом формате, и он режиму не нужен
    useEffect(() => {
        const plan = toParam(state.idcurriculum);
        if (plan) fetchDisciplines(plan);
        else clearDisciplines();
    }, [state.idcurriculum]);

    // xp_key = 0 бэк не принимает (400 «Не указан учебный год»), поэтому без года запроса нет
    const fetchData = (page = 0, filters = state) => {
        if (!toParam(filters.xp_key)) return;
        getComplexTopicsDisciplines({
            xp_key: toParam(filters.xp_key),
            idfaculty: toParam(filters.idfaculty),
            idcurriculum: toParam(filters.idcurriculum),
            cid: toParam(filters.cid),
            page
        });
    };

    // возврат из карточки: список перезапрашивается по сохранённым условиям и странице
    useEffect(() => {
        const snapshot = restored.current;
        if (snapshot?.loaded) fetchData(snapshot.filters.page, snapshot.filters);
    }, []);

    // смена значения сбрасывает нижестоящие поля в «Все»; список при этом не перезагружается
    const handleChange = (name, value) => {
        setState(prev => {
            if (name === 'xp_key') return {...prev, xp_key: value, idfaculty: ALL, idcurriculum: ALL, cid: ALL};
            if (name === 'idfaculty') return {...prev, idfaculty: value, idcurriculum: ALL, cid: ALL};
            if (name === 'idcurriculum') return {...prev, idcurriculum: value, cid: ALL};
            return {...prev, [name]: value};
        });
    };

    const handleRefresh = () => {
        setState(prev => ({...prev, page: 0}));
        setLoaded(true);
        fetchData(0);
    };

    const handlePageChange = (page) => {
        setState(prev => ({...prev, page}));
        fetchData(page);
        window.scrollTo({top: 0, behavior: 'smooth'});
    };

    const isFiltersDirty = state.xp_key !== defaultYear
        || state.idfaculty !== ALL
        || state.idcurriculum !== ALL
        || state.cid !== ALL;

    const handleResetFilters = () => {
        setState(prev => ({...prev, xp_key: defaultYear, idfaculty: ALL, idcurriculum: ALL, cid: ALL}));
    };

    const handleRowClick = (id_ctp) => {
        if (!id_ctp) return;
        history.push({
            pathname: `/complex_topics_disciplines/${id_ctp}`,
            state: { complexDisciplinesList: { filters: state, defaultYear, loaded } }
        });
    };

    // ширина — на обёртке поля, само поле тянется на 100%; подпись рисует CustomSelect (title)
    const renderField = (label, name, options, widthClass) => (
        <div className={`${styles.filterItem} ${widthClass}`}>
            <CustomSelect
                title={label}
                width="100%"
                options={options}
                value={state[name] ?? ALL}
                setFieldValue={handleChange}
                valuesName={name}
            />
        </div>
    );

    // прочерк — только вместо отсутствующего значения: ноль часов выводится нулём
    const renderValue = (value) => (value || value === 0)
        ? value
        : <span className={styles.dash}>—</span>;

    const fromRecord = totalCount ? state.page * itemsPerPage + 1 : 0;
    const toRecord = Math.min((state.page + 1) * itemsPerPage, totalCount);
    const rows = complex || [];
    const currentPage = state.page + 1;

    if (!readRight) return <HaventPermission />;

    return (
        <div className={styles.page}>
            <div className={styles.head}>
                <h1 className={styles.title}>Комплексный тематический план (КТП) дисциплин</h1>
                <div className={styles.headRight}>
                    <button
                        className={styles.resetBtn}
                        onClick={handleResetFilters}
                        disabled={!isFiltersDirty}
                    >
                        Сбросить фильтры
                        <FaRegTrashAlt size={16} />
                    </button>
                </div>
            </div>

            <div className={styles.filters}>
                {renderField('Учебный год', 'xp_key', yearOptions, styles.fYear)}
                {renderField('Факультет', 'idfaculty', facultyOptions, styles.fFac)}
                {renderField('Учебный план', 'idcurriculum', planOptions, styles.fPlan)}
                {renderField('Дисциплина', 'cid', disciplineOptions, styles.fDisc)}
                <button className={styles.refreshBtn} onClick={handleRefresh}>
                    <MdRefresh size={17} />
                    Обновить
                </button>
            </div>

            {isLoading ? (
                <div className={styles.loader}>
                    <Spinner animation="border" role="status">
                        <span className="sr-only">Loading...</span>
                    </Spinner>
                </div>
            ) : errorStatus ? (
                <div className={styles.error}>
                    <div className={styles.errorTitle}>Не удалось загрузить перечень</div>
                    {errorStatus === 403
                        ? 'Доступ к режиму запрещён'
                        : errorStatus === 400
                            ? 'Не указан учебный год'
                            : 'Сервер вернул ошибку'}
                    <div>Повторите попытку по кнопке «Обновить».</div>
                </div>
            ) : (!loaded || rows.length === 0 ? (
                <div className={styles.empty}>
                    <div className={styles.emptyTitle}>По заданным параметрам данные отсутствуют</div>
                    {!loaded && 'Задайте условия отбора и нажмите «Обновить»'}
                </div>
            ) : (
                <>
                    <div className={styles.tableWrap}>
                        <table className={styles.table}>
                            <thead>
                                <tr>
                                    <th>КТП</th>
                                    <th>Учебный план</th>
                                    <th>Дисциплина</th>
                                    <th className={styles.colSt}>Группа</th>
                                    <th className={styles.colSt}>Курс</th>
                                    <th className={styles.colSt}>Семестр</th>
                                    <th className={styles.colHh}>Часов</th>
                                </tr>
                            </thead>
                            <tbody>
                                {/* один и тот же КТП приходит строкой на каждый учебный план — ключ по индексу */}
                                {rows.map((el, ind) =>
                                    <tr key={`${el.id_ctp ?? 'row'}-${ind}`} onClick={() => handleRowClick(el.id_ctp)}>
                                        <td className={styles.strong}>{renderValue(el['КТП'])}</td>
                                        <td className={styles.left}>{renderValue(el['Учебный план'])}</td>
                                        <td className={styles.left}>{renderValue(el['Дисциплина'])}</td>
                                        <td className={`${styles.colSt} ${styles.mono}`}>{renderValue(el['Группа'])}</td>
                                        <td className={styles.colSt}>{renderValue(el['Курс'])}</td>
                                        <td className={styles.colSt}>{renderValue(el['Семестр'])}</td>
                                        <td className={styles.colHh}>{renderValue(el['Часов'])}</td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    </div>

                    <div className={styles.footrow}>
                        <span className={styles.total}>
                            Показано <b>{fromRecord}-{toRecord}</b> из <b>{totalCount}</b> записей
                        </span>
                        <div className={styles.pager}>
                            {pagerItems(currentPage, pagesCount).map((item, ind) => item === 'gap'
                                ? <span key={`gap-${ind}`} className={styles.gap}>…</span>
                                : <button
                                    key={item}
                                    className={item === currentPage ? styles.pageOn : undefined}
                                    onClick={() => handlePageChange(item - 1)}
                                  >
                                    {item}
                                  </button>
                            )}
                        </div>
                    </div>
                </>
            ))}
        </div>
    );
}


export default ComplexTopicsDisciplines;
